import AddOutlined from "@mui/icons-material/AddOutlined";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircle from "@mui/icons-material/CheckCircle";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import MailOutline from "@mui/icons-material/MailOutline";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import TimelapseOutlined from "@mui/icons-material/TimelapseOutlined";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    IconButton,
    Radio,
    InputAdornment,
    LinearProgress,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";

import {
    CHANNELS,
    CHANNEL_STATUS,
    CONTACT_ROLES,
    NOT_INTERESTED,
    SECTIONS,
    channelId,
    fieldError,
    filled,
    formProgress,
    missingAll,
    missingIn,
    sectionState,
    statesFor,
    visible,
    type Answers,
    type Field,
    type Section,
} from "../forms/partnership-form";
import { useCurrentRoomUser } from "../hooks/useCurrentRoomUser";
import { colors, palette, radius } from "../theme/tokens";

import { BasePage } from "./BasePage";

import type { PageTemplateProps } from "./types";

// The in-room Partnership Form (2026-10-28, Elijah): sections down the left,
// overall progress on top, one section at a time, follow-up questions only
// when an answer calls for them. Answers save as you type (only the changed
// ones go up); submit locks the form for staff review. Questions, required
// rules and conditions: forms/partnership-form.ts.

interface FormDoc {
    answers: Answers;
    status: "draft" | "submitted" | "approved";
    submittedAt?: string;
    submittedBy?: string | null;
}

const formKey = (slug: string) => ["room-form", slug] as const;
const SAVE_DELAY_MS = 800;

export function PartnershipForm(props: PageTemplateProps) {
    return (
        <BasePage {...props} compactHeader>
            <FormBody slug={props.room.slug} />
        </BasePage>
    );
}

function FormBody({ slug }: { slug: string }) {
    const qc = useQueryClient();
    const { user } = useCurrentRoomUser();
    const { data, isError } = useQuery({
        queryKey: formKey(slug),
        queryFn: async (): Promise<FormDoc> => {
            const res = await fetch(`/api/rooms/${slug}/form`, { credentials: "include", cache: "no-store" });
            if (!res.ok) throw new Error(`form failed: ${res.status}`);
            return (await res.json()) as FormDoc;
        },
        staleTime: Infinity,
    });

    // Local edits over the loaded answers; null until the first edit.
    const [local, setAnswers] = useState<Answers | null>(null);
    const answers = local ?? data?.answers ?? null;
    const [active, setActive] = useState(0);
    const [saving, setSaving] = useState<"idle" | "saving" | "saved" | "error">("idle");
    const [showMissing, setShowMissing] = useState(false);
    const [busy, setBusy] = useState(false);
    const pending = useRef<Answers>({});
    const timer = useRef<number | undefined>(undefined);

    const flush = useCallback(
        async (keepalive = false) => {
            window.clearTimeout(timer.current);
            const patch = pending.current;
            if (Object.keys(patch).length === 0) return;
            pending.current = {};
            setSaving("saving");
            try {
                const res = await fetch(`/api/rooms/${slug}/form`, {
                    method: "PATCH",
                    credentials: "include",
                    keepalive,
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ answers: patch }),
                });
                const body = (await res.json().catch(() => null)) as (FormDoc & { form?: FormDoc }) | null;
                if (res.status === 409 && body?.form) {
                    qc.setQueryData(formKey(slug), body.form);
                    setAnswers(body.form.answers);
                }
                setSaving(res.ok ? "saved" : "error");
            } catch {
                pending.current = { ...patch, ...pending.current };
                setSaving("error");
            }
        },
        [slug, qc],
    );

    // Save anything still waiting when the reader leaves the page or the tab.
    useEffect(() => {
        const onHide = () => void flush(true);
        window.addEventListener("pagehide", onHide);
        return () => {
            window.removeEventListener("pagehide", onHide);
            void flush(true);
        };
    }, [flush]);

    if (isError) return <Alert severity="warning">The form could not be loaded. Refresh the page to try again.</Alert>;
    if (!data || !answers) return <LinearProgress />;

    const locked = data.status !== "draft";
    const set = (id: string, value: unknown) => {
        setAnswers((a) => ({ ...(a ?? data.answers), [id]: value }));
        pending.current[id] = value === "" ? null : value;
        setSaving("saving");
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => void flush(), SAVE_DELAY_MS);
    };

    const done = SECTIONS.filter((s) => sectionState(s, answers) === "done").length;
    const missing = missingAll(answers);
    const section = SECTIONS[active];

    const go = (i: number) => {
        void flush();
        setActive(i);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const act = async (action: "submit" | "approve" | "reopen") => {
        await flush();
        setBusy(true);
        try {
            const res = await fetch(`/api/rooms/${slug}/form/${action}`, { method: "POST", credentials: "include" });
            const body = (await res.json().catch(() => null)) as (FormDoc & { form?: FormDoc }) | null;
            const doc = res.ok ? body : body?.form;
            if (doc) {
                qc.setQueryData(formKey(slug), doc);
                setAnswers(doc.answers);
            }
            if (!res.ok) setShowMissing(true);
            // The checklist step moves with the form.
            void qc.invalidateQueries({ queryKey: ["room-state", slug] });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                        {done} of {SECTIONS.length} sections complete
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {locked ? "" : saving === "saving" ? "Saving..." : saving === "saved" ? "All changes saved" : saving === "error" ? "Not saved, check your connection" : ""}
                    </Typography>
                </Stack>
                <LinearProgress variant="determinate" value={formProgress(answers)} sx={{ height: 8, borderRadius: radius.sm, bgcolor: PORTAL.border, "& .MuiLinearProgress-bar": { bgcolor: "#4169E1" } }} />
            </Box>

            {locked && <StatusBanner doc={data} staff={!!user?.staff} busy={busy} onAction={act} />}

            <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 2, md: 4 }} alignItems={{ xs: "stretch", md: "flex-start" }}>
                <Box
                    component="nav"
                    sx={{
                        flex: { md: "0 0 240px" },
                        position: { md: "sticky" },
                        top: { md: 24 },
                        display: "flex",
                        flexDirection: { xs: "row", md: "column" },
                        overflowX: { xs: "auto", md: "visible" },
                        gap: 0.5,
                    }}
                >
                    {SECTIONS.map((s, i) => {
                        const state = sectionState(s, answers);
                        return (
                            <Box
                                key={s.id}
                                component="button"
                                type="button"
                                onClick={() => go(i)}
                                sx={{
                                    all: "unset",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.25,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: radius.sm,
                                    whiteSpace: "nowrap",
                                    fontSize: 15,
                                    fontWeight: i === active ? 600 : 400,
                                    color: colors.emphasis,
                                    bgcolor: i === active ? palette.infoBg : "transparent",
                                    "&:hover": { bgcolor: i === active ? palette.infoBg : colors.lightGray },
                                    "&:focus-visible": { outline: `2px solid ${colors.navy}` },
                                }}
                            >
                                {state === "done" ? (
                                    <CheckCircle sx={{ fontSize: 20, color: palette.green }} />
                                ) : state === "partial" ? (
                                    <TimelapseOutlined sx={{ fontSize: 20, color: palette.blue }} />
                                ) : (
                                    <RadioButtonUnchecked sx={{ fontSize: 20, color: colors.mediumGray }} />
                                )}
                                {s.title}
                            </Box>
                        );
                    })}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0, maxWidth: section.id === "contacts" ? "none" : 760 }}>
                    <Typography variant="h2" sx={{ fontSize: 24, fontWeight: 800, mb: section.intro ? 1 : 3 }}>
                        {section.title}
                    </Typography>
                    {section.intro && (
                        <Typography sx={{ color: "text.secondary", mb: 3 }}>{section.intro}</Typography>
                    )}

                    {section.id === "channels" ? (
                        <ChannelGrid answers={answers} onChange={set} disabled={locked} />
                    ) : section.id === "contacts" ? (
                        <ContactGrid section={section} answers={answers} onChange={set} disabled={locked} />
                    ) : (
                        <FieldList fields={section.fields} values={answers} scope={answers} onChange={set} disabled={locked} />
                    )}

                    {showMissing && missingIn(section, answers).length > 0 && (
                        <Alert severity="warning" sx={{ mt: 3 }}>
                            Still needed here: {missingIn(section, answers).join(", ")}.
                        </Alert>
                    )}

                    <Stack direction="row" spacing={2} sx={{ mt: 4 }} justifyContent="space-between" flexWrap="wrap" useFlexGap>
                        <Button variant="outlined" disabled={active === 0} onClick={() => go(active - 1)}>
                            Back
                        </Button>
                        {active < SECTIONS.length - 1 ? (
                            <Button variant="contained" onClick={() => go(active + 1)}>
                                Next section
                            </Button>
                        ) : (
                            !locked && (
                                <Button variant="contained" disabled={busy} onClick={() => (missing.length ? setShowMissing(true) : void act("submit"))}>
                                    Submit the form
                                </Button>
                            )
                        )}
                    </Stack>

                    {!locked && active === SECTIONS.length - 1 && showMissing && missing.length > 0 && (
                        <Alert severity="warning" sx={{ mt: 3 }}>
                            <Typography sx={{ fontWeight: 600, mb: 1 }}>The form can be submitted once these are answered:</Typography>
                            {SECTIONS.map((s, i) => {
                                const m = missingIn(s, answers);
                                return m.length ? (
                                    <Box key={s.id} sx={{ mb: 0.5 }}>
                                        <Box component="button" type="button" onClick={() => go(i)} sx={{ all: "unset", cursor: "pointer", fontWeight: 600, textDecoration: "underline" }}>
                                            {s.title}
                                        </Box>
                                        : {m.join(", ")}
                                    </Box>
                                ) : null;
                            })}
                        </Alert>
                    )}
                </Box>
            </Stack>
        </Box>
    );
}

function StatusBanner({ doc, staff, busy, onAction }: { doc: FormDoc; staff: boolean; busy: boolean; onAction: (a: "approve" | "reopen") => void }) {
    const when = doc.submittedAt ? new Date(doc.submittedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
    const text =
        doc.status === "approved"
            ? "Bison has reviewed and approved this form. Get in touch with us if anything changes."
            : `Submitted ${when}${doc.submittedBy ? ` by ${doc.submittedBy}` : ""}. Bison is reviewing your answers. Get in touch with us if anything changes.`;
    return (
        <Alert
            severity="info"
            sx={{ mb: 3 }}
            action={
                staff && (
                    <Stack direction="row" spacing={1}>
                        {doc.status === "submitted" && (
                            <Button size="small" variant="contained" disabled={busy} onClick={() => onAction("approve")}>
                                Approve
                            </Button>
                        )}
                        <Button size="small" variant="outlined" disabled={busy} onClick={() => onAction("reopen")}>
                            Reopen
                        </Button>
                    </Stack>
                )
            }
        >
            {text}
        </Alert>
    );
}

// One list of fields. `scope` is what conditions read: the whole form at the
// top level, the row inside a repeat.
function FieldList({
    fields,
    values,
    scope,
    onChange,
    disabled,
}: {
    fields: Field[];
    values: Answers;
    scope: Answers;
    onChange: (id: string, v: unknown) => void;
    disabled: boolean;
}) {
    return (
        <Stack spacing={2.5}>
            {fields.filter((f) => visible(f, scope)).map((f) => (
                <Box key={f.id}>
                    {f.group && (
                        <Typography sx={{ fontWeight: 700, color: colors.emphasis, mt: 2, mb: 1.5 }}>{f.group}</Typography>
                    )}
                    <FieldInput field={f} value={values[f.id]} scope={scope} onChange={(v) => onChange(f.id, v)} disabled={disabled} />
                </Box>
            ))}
        </Stack>
    );
}

// A number box without the browser's up/down arrows.
const NO_SPINNER = {
    "& input[type=number]": { MozAppearance: "textfield" },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
};

function FieldInput({
    field: f,
    value,
    scope,
    onChange,
    disabled,
}: {
    field: Field;
    value: unknown;
    scope: Answers;
    onChange: (v: unknown) => void;
    disabled: boolean;
}) {
    const required = typeof f.required === "function" ? f.required(scope) : !!f.required;

    if (f.type === "check") {
        return <FormControlLabel control={<Checkbox size="small" sx={{ py: 0.25 }} checked={value === true} disabled={disabled} onChange={(e) => onChange(e.target.checked || null)} />} label={f.label} />;
    }

    if (f.type === "multi") {
        const list = Array.isArray(value) ? (value as string[]) : [];
        return (
            <Box>
                <Typography sx={{ fontSize: 13.2, lineHeight: "18px", fontWeight: 500, color: PORTAL.label, mb: 0.5 }}>
                    {f.label}
                    {required && <Box component="span" sx={{ color: PORTAL.error, ml: 0.25 }}>*</Box>}
                </Typography>
                <FormGroup>
                    {f.options?.map((o) => (
                        <FormControlLabel
                            key={o}
                            label={o}
                            control={
                                <Checkbox
                                    size="small"
                                    sx={{ py: 0.25 }}
                                    checked={list.includes(o)}
                                    disabled={disabled}
                                    onChange={(e) => onChange(e.target.checked ? [...list, o] : list.filter((x) => x !== o))}
                                />
                            }
                        />
                    ))}
                </FormGroup>
            </Box>
        );
    }

    if (f.type === "repeat") {
        const stored = Array.isArray(value) ? (value as Answers[]) : [];
        const rows: Answers[] = f.flat && !stored.length ? [{}] : stored;
        const setRow = (i: number, id: string, v: unknown) => onChange(rows.map((r, j) => (j === i ? { ...r, [id]: v === "" ? undefined : v } : r)));
        return (
            <Stack spacing={f.flat ? 3 : 2}>
                {rows.map((row, i) => {
                    const title = f.flat ? (typeof row.name === "string" ? row.name : "") : `${f.label} ${i + 1}`;
                    const removable = !disabled && (!f.flat || rows.length > 1);
                    return (
                        <Box key={i} sx={f.flat ? (i ? { borderTop: `1px solid ${colors.line}`, pt: 3 } : undefined) : { border: `1px solid ${colors.line}`, borderRadius: radius.md, p: 2.5 }}>
                            {(title || removable) && (
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                    <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
                                    {removable && (
                                        <IconButton aria-label={`Remove ${f.label.toLowerCase()} ${i + 1}`} onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                                            <DeleteOutlineOutlined />
                                        </IconButton>
                                    )}
                                </Stack>
                            )}
                            <FieldList fields={f.fields ?? []} values={row} scope={row} onChange={(id, v) => setRow(i, id, v)} disabled={disabled} />
                        </Box>
                    );
                })}
                {!disabled && (
                    <Box>
                        <Button variant="outlined" startIcon={<AddOutlined />} onClick={() => onChange([...rows, {}])}>
                            Add {rows.length ? "another" : "a"} {f.label.toLowerCase()}
                        </Button>
                    </Box>
                )}
            </Stack>
        );
    }

    // The state list follows the country picked beside it (the portal's lists).
    // Pickup to offers only slots after pickup from, as the portal does.
    const options =
        f.id === "state" ? statesFor(scope.country) : f.id === "pickupTo" && scope.pickupFrom ? f.options?.filter((o) => o > String(scope.pickupFrom)) : f.options;
    const id = `pf-${f.id}`;
    if (options) {
        // The open list leaves out what the box already shows: no blank row until
        // answered, then blank plus the other choices. Hidden, not removed, so the box can still show it.
        const current = typeof value === "string" ? value : "";
        const hideIfCurrent = (o: string) => (o === current ? { display: "none" } : undefined);
        return (
            <Labeled id={id} label={f.label} required={required} helper={f.hint}>
                <TextField
                    select
                    fullWidth
                    id={id}
                    disabled={disabled}
                    sx={INPUT}
                    value={current}
                    onChange={(e) => onChange(e.target.value)}
                    slotProps={{ select: { IconComponent: KeyboardArrowDown, displayEmpty: true }, htmlInput: NO_AUTOFILL }}
                >
                    {/* A blank row clears the answer. */}
                    <MenuItem value="" aria-label="Clear" sx={hideIfCurrent("")}>
                        &nbsp;
                    </MenuItem>
                    {options.map((o) => (
                        <MenuItem key={o} value={o} sx={hideIfCurrent(o)}>
                            {o}
                        </MenuItem>
                    ))}
                </TextField>
            </Labeled>
        );
    }

    const error = fieldError(f, value, scope);
    const Icon = TYPE_ICON[f.type];
    return (
        <Labeled id={id} label={f.label} required={required} helper={error ?? f.hint} error={!!error}>
            <TextField
                fullWidth
                id={id}
                disabled={disabled}
                multiline={f.type === "longtext"}
                minRows={f.type === "longtext" ? 3 : undefined}
                type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "email" ? "email" : f.type === "url" ? "url" : f.type === "phone" ? "tel" : "text"}
                slotProps={{
                    htmlInput: {
                        ...NO_AUTOFILL,
                        min: f.min,
                        max: f.max,
                        step: f.integer ? 1 : undefined,
                        maxLength: f.maxLength,
                        // The calendar icon sits on the left, so a click anywhere opens the native picker.
                        onClick: f.type === "date" ? (e: MouseEvent<HTMLInputElement>) => e.currentTarget.showPicker?.() : undefined,
                    },
                    input: Icon ? { startAdornment: <InputAdornment position="start"><Icon sx={{ fontSize: 16, color: PORTAL.muted }} /></InputAdornment> } : undefined,
                }}
                sx={f.noSpinner ? { ...INPUT, ...NO_SPINNER } : INPUT}
                value={value === undefined || value === null ? "" : String(value)}
                error={!!error}
                onChange={(e) => onChange(f.type === "number" && e.target.value !== "" ? Number(e.target.value) : e.target.value)}
            />
        </Labeled>
    );
}

// Field look copied from the supplier portal's form controls (bison-commerce
// libs/ui: input.tsx, label.tsx, forms/internal/field-wrapper.tsx): a small label
// above a white 36px box, 1px light border, 6px corners, no shadow, a type icon
// inside on the left and a chevron on dropdowns (Elijah, 2026-10-29).
const PORTAL = { label: "#3b3947", muted: "#6c6b75", border: "#e6e6e8", error: "#ea0621", disabled: "#e6e6e8" };
const INPUT = {
    "& .MuiOutlinedInput-root": {
        bgcolor: "#fff",
        borderRadius: "6px",
        fontSize: 14,
        boxShadow: "none",
        "& fieldset": { borderColor: PORTAL.border, borderWidth: "1px" },
        "&:hover fieldset": { borderColor: PORTAL.border },
        "&.Mui-focused fieldset": { borderColor: colors.navy, borderWidth: "1px" },
        "&.Mui-focused": { boxShadow: `0 0 0 3px ${colors.navy}33` },
        "&.Mui-error fieldset": { borderColor: PORTAL.error },
        "&.Mui-disabled": { bgcolor: PORTAL.disabled },
    },
    "& .MuiOutlinedInput-input": { py: "8.5px", px: "12px", minHeight: "unset" },
    "& .MuiInputBase-inputAdornedStart": { pl: 0 },
    "& .MuiInputAdornment-positionStart": { mr: 1 },
    "& .MuiOutlinedInput-root:has(.MuiInputAdornment-positionStart)": { pl: "12px" },
    "& .MuiInputBase-multiline": { p: 0 },
    "& .MuiSelect-icon": { fontSize: 18, color: PORTAL.muted, right: 10 },
    "& input[type=date]::-webkit-calendar-picker-indicator": { display: "none" },
};
// Channels as one grid (Elijah, 2026-10-29): a row per marketplace, a radio
// column per status, and a notes column. Same answer keys as the select fields.

function ChannelGrid({ answers, onChange, disabled }: { answers: Answers; onChange: (id: string, v: unknown) => void; disabled: boolean }) {
    const cell = { borderBottom: `1px solid ${PORTAL.border}`, px: 1, py: 0.75, fontSize: 13 };
    // Vertical rules set off the marketplace names, the Not applicable column and the notes.
    const rule = `1px solid ${PORTAL.border}`;
    const statusRule = (o: string) => (o === "Not applicable" ? { borderLeft: rule } : undefined);
    return (
        <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", minWidth: 760 }}>
                <thead>
                    <tr>
                        <Box component="th" sx={{ ...cell, borderRight: rule, textAlign: "left", fontWeight: 500, color: PORTAL.label, fontSize: 12 }}>Marketplace</Box>
                        {CHANNEL_STATUS.map((h) => (
                            <Box component="th" key={h} sx={{ ...cell, ...statusRule(h), fontWeight: 500, color: PORTAL.label, fontSize: 12, width: 92, lineHeight: "16px" }}>
                                {h}
                            </Box>
                        ))}
                        <Box component="th" sx={{ ...cell, borderLeft: rule, textAlign: "left", fontWeight: 500, color: PORTAL.label, fontSize: 12, width: 220 }}>Notes</Box>
                    </tr>
                </thead>
                <tbody>
                    {CHANNELS.map((c) => {
                        const id = channelId(c);
                        const status = answers[id];
                        const needsReason = status === NOT_INTERESTED && !answers[`${id}_note`];
                        return (
                            <tr key={c}>
                                <Box component="td" sx={{ ...cell, borderRight: rule, fontWeight: 500, whiteSpace: "nowrap" }}>
                                    {c}
                                    <Box component="span" sx={{ color: PORTAL.error, ml: 0.25 }}>*</Box>
                                </Box>
                                {CHANNEL_STATUS.map((o) => (
                                    <Box component="td" key={o} sx={{ ...cell, ...statusRule(o), textAlign: "center" }}>
                                        <Radio
                                            size="small"
                                            sx={{ p: 0.5 }}
                                            checked={status === o}
                                            disabled={disabled}
                                            onChange={() => onChange(id, o)}
                                            slotProps={{ input: { "aria-label": `${c}: ${o}` } }}
                                        />
                                    </Box>
                                ))}
                                <Box component="td" sx={{ ...cell, borderLeft: rule }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={disabled}
                                        placeholder={needsReason ? "Tell us why" : ""}
                                        error={needsReason}
                                        sx={INPUT}
                                        value={typeof answers[`${id}_note`] === "string" ? (answers[`${id}_note`] as string) : ""}
                                        onChange={(e) => onChange(`${id}_note`, e.target.value)}
                                        slotProps={{ htmlInput: { ...NO_AUTOFILL, "aria-label": `Notes on ${c}` } }}
                                    />
                                </Box>
                            </tr>
                        );
                    })}
                </tbody>
            </Box>
        </Box>
    );
}

// Contacts as one grid (Elijah, 2026-11-02): a row per responsibility. A ticked
// "Same as primary" shows the primary contact's details, locked, in that row.
function ContactGrid({ section, answers, onChange, disabled }: { section: Section; answers: Answers; onChange: (id: string, v: unknown) => void; disabled: boolean }) {
    const cell = { borderBottom: `1px solid ${PORTAL.border}`, px: 1, py: 0.75, fontSize: 13, verticalAlign: "top" };
    const head = { ...cell, textAlign: "left", fontWeight: 500, color: PORTAL.label, fontSize: 12, lineHeight: "16px" };
    const byId = Object.fromEntries(section.fields.map((f) => [f.id, f]));
    const cols = ["name", "title", "email", "phone"];
    const rows: readonly (readonly [string, string])[] = [["primary", "Primary contact"], ...CONTACT_ROLES];
    return (
        <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", minWidth: 900 }}>
                <thead>
                    <tr>
                        <Box component="th" sx={{ ...head, width: 150 }}>Responsibilities</Box>
                        <Box component="th" sx={{ ...head, width: 72, textAlign: "center" }}>Same as primary</Box>
                        {cols.map((c) => (
                            <Box component="th" key={c} sx={head}>{byId[`primary_${c}`].label}</Box>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(([key, label]) => {
                        const isPrimary = key === "primary";
                        const same = !isPrimary && answers[`${key}_same`] === "Yes";
                        return (
                            <tr key={key}>
                                <Box component="td" sx={{ ...cell, fontWeight: 500, lineHeight: "36px", whiteSpace: "nowrap" }}>{label}</Box>
                                <Box component="td" sx={{ ...cell, textAlign: "center" }}>
                                    {!isPrimary && (
                                        <Checkbox
                                            size="small"
                                            checked={same}
                                            disabled={disabled}
                                            onChange={(e) => onChange(`${key}_same`, e.target.checked ? "Yes" : null)}
                                            slotProps={{ input: { "aria-label": `${label}: same as primary` } }}
                                        />
                                    )}
                                </Box>
                                {cols.map((c) => {
                                    const f = byId[`${key}_${c}`];
                                    const value = answers[same ? `primary_${c}` : f.id];
                                    const required = !same && (typeof f.required === "function" ? f.required(answers) : !!f.required);
                                    const error = same ? null : fieldError(f, value, answers);
                                    return (
                                        <Box component="td" key={c} sx={cell}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                disabled={disabled || same}
                                                type={f.type === "email" ? "email" : f.type === "phone" ? "tel" : "text"}
                                                placeholder={required && !filled(value) ? "Required" : ""}
                                                error={!!error}
                                                sx={INPUT}
                                                value={value === undefined || value === null ? "" : String(value)}
                                                onChange={(e) => onChange(f.id, e.target.value)}
                                                slotProps={{ htmlInput: { ...NO_AUTOFILL, maxLength: f.maxLength, "aria-label": `${label}: ${f.label}` } }}
                                            />
                                            {error && <Typography sx={{ fontSize: 12, lineHeight: "16px", color: PORTAL.error, mt: 0.5 }}>{error}</Typography>}
                                        </Box>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </Box>
        </Box>
    );
}

// Keeps password managers (1Password, LastPass, Bitwarden, Dashlane) and the
// browser's own autofill from offering to fill or save these answers.
const NO_AUTOFILL = {
    autoComplete: "off",
    "data-1p-ignore": true,
    "data-lpignore": "true",
    "data-bwignore": true,
    "data-form-type": "other",
};
const TYPE_ICON: Partial<Record<Field["type"], typeof MailOutline>> = {
    email: MailOutline,
    phone: PhoneOutlined,
    date: CalendarTodayOutlined,
};

function Labeled({ id, label, required, helper, error, children }: { id: string; label: string; required: boolean; helper?: string; error?: boolean; children: ReactNode }) {
    return (
        <Stack spacing={0.5}>
            <Typography component="label" htmlFor={id} sx={{ fontSize: 13.2, lineHeight: "18px", fontWeight: 500, color: error ? PORTAL.error : PORTAL.label }}>
                {label}
                {required && <Box component="span" sx={{ color: PORTAL.error, ml: 0.25 }}>*</Box>}
            </Typography>
            {children}
            {helper && <Typography sx={{ fontSize: 12, lineHeight: "16px", color: error ? PORTAL.error : PORTAL.muted }}>{helper}</Typography>}
        </Stack>
    );
}
